import React, { useMemo, useState } from 'react';
import { ChevronsUpDown, X, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';

/**
 * Multi-select "collaborators" picker built on Shadcn Command + Popover.
 * Shows selected users as chips above the trigger, with typeahead search below.
 *
 * Props:
 *  - users: Array<{ id, name, role, email? }> full team list
 *  - value: string[] of selected user_ids
 *  - onChange: (nextIds: string[]) => void
 *  - exclude: string[] of user_ids to hide from picker (typically the assignee/owner)
 *  - placeholder: trigger label when no chips
 *  - testIdPrefix
 *  - disabled
 */
export default function CollaboratorPicker({
  users = [],
  value = [],
  onChange,
  exclude = [],
  placeholder = 'Add collaborators…',
  testIdPrefix = 'collab-picker',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  // Only internal users (Clients cannot collaborate) and drop excluded ids.
  const pickable = useMemo(() => {
    const excludeSet = new Set(exclude || []);
    return (users || []).filter((u) => u.role !== 'Client' && !excludeSet.has(u.id));
  }, [users, exclude]);

  const byId = useMemo(() => {
    const m = new Map();
    (users || []).forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);

  const selectedChips = (value || [])
    .map((id) => byId.get(id))
    .filter(Boolean);

  const toggle = (id) => {
    if (!onChange) return;
    const set = new Set(value || []);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange(Array.from(set));
  };

  const removeChip = (id, e) => {
    e?.stopPropagation?.();
    if (!onChange) return;
    onChange((value || []).filter((v) => v !== id));
  };

  const initials = (name) => (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');

  return (
    <div className="space-y-2">
      {/* Selected chips */}
      {selectedChips.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-chips`}>
          {selectedChips.map((u) => (
            <Badge
              key={u.id}
              variant="outline"
              className="border-amber-500/40 bg-amber-500/5 text-amber-200 pl-1.5 pr-1 py-0.5 gap-1 text-[11px] font-medium"
              data-testid={`${testIdPrefix}-chip-${u.id}`}
            >
              <span className="inline-flex h-4 w-4 rounded-full bg-amber-500/20 items-center justify-center text-[9px] font-bold text-amber-100">
                {initials(u.name)}
              </span>
              <span className="truncate max-w-[140px]">{u.name}</span>
              <button
                type="button"
                onClick={(e) => removeChip(u.id, e)}
                aria-label={`Remove ${u.name}`}
                className="ml-0.5 rounded hover:bg-amber-500/20 text-amber-200/70 hover:text-red-300 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400"
                data-testid={`${testIdPrefix}-chip-remove-${u.id}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Trigger */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            data-testid={`${testIdPrefix}-trigger`}
            className="w-full justify-between bg-[#0f2240] border-amber-500/20 hover:bg-[#0f2240] hover:border-amber-500/40 text-white font-normal"
          >
            <span className="flex items-center gap-2 truncate">
              <Users className="h-3.5 w-3.5 text-amber-300 flex-shrink-0" />
              <span className={selectedChips.length ? 'text-slate-300' : 'text-slate-400'}>
                {selectedChips.length ? `${selectedChips.length} collaborator${selectedChips.length > 1 ? 's' : ''}` : placeholder}
              </span>
            </span>
            <ChevronsUpDown className="h-4 w-4 opacity-60 flex-shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 bg-[#0a1628] border-amber-500/20 text-slate-100"
          style={{ width: 'var(--radix-popover-trigger-width)' }}
        >
          <Command className="bg-transparent">
            <CommandInput
              placeholder="Search team members…"
              className="text-slate-100 placeholder:text-slate-500"
              data-testid={`${testIdPrefix}-search`}
            />
            <CommandList>
              <CommandEmpty className="text-slate-400 py-4 text-sm text-center">No teammates found.</CommandEmpty>
              <CommandGroup>
                {pickable.map((u) => {
                  const isSelected = (value || []).includes(u.id);
                  return (
                    <CommandItem
                      key={u.id}
                      value={`${u.name} ${u.role} ${u.email || ''}`}
                      onSelect={() => toggle(u.id)}
                      data-testid={`${testIdPrefix}-option-${u.id}`}
                      className={`cursor-pointer text-slate-100 aria-selected:bg-amber-500/15 aria-selected:text-white ${isSelected ? 'bg-amber-500/5' : ''}`}
                    >
                      <span className={`mr-2 h-4 w-4 rounded border flex-shrink-0 flex items-center justify-center text-[10px] ${isSelected ? 'bg-amber-500 border-amber-500 text-[#0a1628] font-bold' : 'border-slate-500/50'}`}>
                        {isSelected ? '✓' : ''}
                      </span>
                      <span className="inline-flex h-5 w-5 rounded-full bg-amber-500/15 items-center justify-center text-[9px] font-bold text-amber-100 mr-2 flex-shrink-0">
                        {initials(u.name)}
                      </span>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="truncate font-medium">{u.name}</span>
                        <span className="text-[11px] text-slate-400 truncate">{u.role}</span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
